import { useTranslation } from 'react-i18next';
import { Heart, Pencil, Trash2, Star, Gem } from 'lucide-react';
import { HotWheel } from '../../types/hotwheels.types';
import { useFileUrl } from '@/lib/api/hooks/use-storage';

/**
 * HotWheel card for the collection grid.
 *
 * - Full-bleed image at top (or red placeholder)
 * - Brand + ModelNumber + Series
 * - Release year + condition chip
 * - Hover overlay: edit/delete buttons + favorite star
 * - Treasure Hunt badge if applicable
 */

interface HotWheelCardProps {
  car: HotWheel;
  onEdit: (car: HotWheel) => void;
  onDelete: (car: HotWheel) => void;
  onToggleFavorite?: (car: HotWheel) => void;
}

export function HotWheelCard({ car, onEdit, onDelete, onToggleFavorite }: HotWheelCardProps) {
  const { t } = useTranslation();
  const { data: fileRes } = useFileUrl(car.MainImage ?? null);
  const imageUrl = fileRes?.url;

  const showThBadge = car.TreasureHunt || car.SuperTreasureHunt;

  return (
    <div className="group relative rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-blue-500/20 transition-all hover:-translate-y-1">
      {/* Image area */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-zinc-800 to-zinc-950 overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`${car.Brand} ${car.ModelNumber}`}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600">
            <Gem className="h-16 w-16" />
          </div>
        )}

        {/* Top-left: Treasure Hunt badge */}
        {showThBadge && (
          <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-yellow-500/95 px-2 py-1 text-xs font-bold text-black shadow">
            <Star className="h-3 w-3 fill-current" />
            {car.SuperTreasureHunt ? 'STH' : 'TH'}
          </div>
        )}

        {/* Top-right: limited edition badge */}
        {car.LimitedEdition && (
          <div className="absolute top-2 right-2 rounded-full bg-[#2563EB]/95 px-2 py-1 text-xs font-bold text-white shadow">
            LE
          </div>
        )}

        {/* Hover overlay: edit + delete + favorite */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          {onToggleFavorite && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(car);
              }}
              className="rounded-full bg-white/95 p-3 hover:bg-white transition shadow"
              title={t('FAVORITE')}
            >
              <Heart
                className={`h-5 w-5 ${car.Favorite ? 'fill-blue-500 text-blue-500' : 'text-zinc-700'}`}
              />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(car);
            }}
            className="rounded-full bg-white/95 p-3 hover:bg-white transition shadow"
            title={t('EDIT_CAR')}
          >
            <Pencil className="h-5 w-5 text-zinc-800" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(car);
            }}
            className="rounded-full bg-white/95 p-3 hover:bg-white transition shadow"
            title={t('DELETE_CAR')}
          >
            <Trash2 className="h-5 w-5 text-blue-500" />
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="p-4 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-zinc-400 uppercase tracking-wider truncate">
              {car.Brand} · {car.Series}
            </div>
            <div className="text-lg font-bold text-white truncate">
              {car.ModelNumber}
            </div>
          </div>
          {car.Favorite && (
            <Heart className="h-4 w-4 fill-blue-500 text-blue-500 flex-shrink-0 mt-1" />
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-zinc-400 pt-1">
          <span className="rounded-full bg-zinc-800 px-2 py-0.5">{car.ReleaseYear}</span>
          {car.Condition && (
            <span className="rounded-full bg-zinc-800 px-2 py-0.5">{car.Condition}</span>
          )}
          {car.Color && (
            <span className="rounded-full bg-zinc-800 px-2 py-0.5 truncate">{car.Color}</span>
          )}
        </div>

        {typeof car.EstimatedValue === 'number' && car.EstimatedValue > 0 && (
          <div className="pt-2 flex items-baseline gap-1">
            <span className="text-xs text-zinc-500">{t('ESTIMATED_VALUE')}</span>
            <span className="text-base font-semibold text-[#2563EB]">
              ${car.EstimatedValue.toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}